/**
 * The real (browser-only) glTF load function for the asset loader (WR.3.2),
 * wrapping Three.js `GLTFLoader` with Draco mesh decompression and KTX2/Basis
 * texture transcoding (asset-pipeline.md delivery format). It is injected into
 * `AssetLoader` as a `GltfLoadFn`; it is deliberately NOT imported by the loader
 * core or the unit tests, which stay GPU-free (§12 risk 1). It is type-checked
 * by the frontend build and wired into the scene builder in WR.3.3.
 *
 * Decoder/transcoder binaries are served from our own origin (decoderPaths.ts;
 * cdn-static-offload.md S6), never a third-party CDN. KTX2 support detection needs
 * the live renderer, passed in by the scene builder (WR.3.3).
 */

import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

import type { Mesh, Object3D } from 'three';

import type { GameRenderer } from '../renderer.js';
import { GLTF_MATERIAL_USERDATA_KEY } from './emitters.js';
import type { GltfLoadFn, GltfResult } from './loader.js';
import { DRACO_DECODER_PATH, KTX2_TRANSCODER_PATH } from './decoderPaths.js';
import { type AssetCdnFallback, sessionAssetCdnFallback } from './assetCdnFallback.js';

export interface ThreeGltfLoaderOptions {
  /** The CDN fallback (cdn-static-offload.md S3); default: the session's (`sessionAssetCdnFallback`). */
  cdnFallback?: AssetCdnFallback;
  /** The live, initialized renderer — required for KTX2 support detection. */
  renderer?: GameRenderer;
  /** Override the Draco decoder directory (default: same-origin, decoderPaths.ts). */
  dracoDecoderPath?: string;
  /** Override the KTX2/Basis transcoder directory (default: same-origin, decoderPaths.ts). */
  ktx2TranscoderPath?: string;
}

/**
 * ONE KTX2Loader per (renderer, transcoder path), shared by every `GLTFLoader` this
 * module builds. `Game` builds a glTF loader per subsystem (scene builder, node
 * placer, creatures, equipment, rigs, …); each used to own a KTX2Loader, so a zone
 * whose scatter AND nodes are KTX2 (the Measured Yard after the 2026-10-19 flora
 * retexture) started two Basis transcoder worker pools and three warned "Multiple
 * active KTX2Loader instances". A KTX2Loader is stateless per load apart from its
 * worker pool and the support config `detectSupport` bakes in at first init, so
 * sharing it per renderer is exact. A different renderer (renderer-lost recovery, a
 * WebGPU fork) gets its own, keyed weakly so a dropped renderer frees its entry.
 */
const sharedKtx2ByRenderer = new WeakMap<object, Map<string, KTX2Loader>>();
const sharedKtx2NoRenderer = new Map<string, KTX2Loader>();

export function sharedKtx2Loader(path: string, renderer: GameRenderer | undefined): KTX2Loader {
  let byPath = sharedKtx2NoRenderer;
  if (renderer) {
    byPath = sharedKtx2ByRenderer.get(renderer) ?? new Map<string, KTX2Loader>();
    sharedKtx2ByRenderer.set(renderer, byPath);
  }
  let loader = byPath.get(path);
  if (!loader) {
    loader = new KTX2Loader();
    loader.setTranscoderPath(path);
    if (renderer) loader.detectSupport(renderer);
    byPath.set(path, loader);
  }
  return loader;
}

/**
 * q-npc-props-adoption-and-flame: stamp each mesh with the glTF MATERIAL INDEX it was built
 * from (`userData.gltfMaterial`), so an asset pack's emitter `hide` rule can name the static
 * flame by index. GLTFLoader records the index on its `associations` map — including for the
 * cached material clones it makes per primitive — and drops it from the scene graph.
 */
export function stampGltfMaterialIndices(
  scene: Object3D,
  associations: ReadonlyMap<unknown, { materials?: number }>,
): void {
  scene.traverse((node) => {
    const material = (node as Mesh).isMesh ? (node as Mesh).material : undefined;
    if (material === undefined || Array.isArray(material)) return;
    const index = associations.get(material)?.materials;
    if (typeof index === 'number') node.userData[GLTF_MATERIAL_USERDATA_KEY] = index;
  });
}

/**
 * Build a `GltfLoadFn` backed by a configured `GLTFLoader`. Construct ONE per
 * client session and inject it into the `AssetLoader` (the Draco loader holds a
 * worker pool; reuse avoids re-spawning it). The KTX2 transcoder is shared across
 * every loader built for the same renderer (`sharedKtx2Loader`).
 */
export function createThreeGltfLoader(options: ThreeGltfLoaderOptions = {}): GltfLoadFn {
  const gltfLoader = new GLTFLoader();
  // A CDN failure retries once from the game's own origin and pins the session there (S3).
  const cdnFallback = options.cdnFallback ?? sessionAssetCdnFallback();

  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(options.dracoDecoderPath ?? DRACO_DECODER_PATH);
  gltfLoader.setDRACOLoader(dracoLoader);

  gltfLoader.setKTX2Loader(
    sharedKtx2Loader(options.ktx2TranscoderPath ?? KTX2_TRANSCODER_PATH, options.renderer),
  );

  // Meshopt geometry decompression — required for packs compressed with
  // `gltf-transform optimize --compress meshopt` (the default in asset-compress).
  // Without this, meshopt-encoded GLBs load as empty meshes with no error.
  gltfLoader.setMeshoptDecoder(MeshoptDecoder);

  // Map explicitly rather than returning the GLTF verbatim: `animations` is the
  // half the loader used to drop on the floor (AP5.S1 — the rig rendered but
  // never moved), so the mapping is stated here where it can't silently regress.
  return async (url: string): Promise<GltfResult> => {
    const gltf = await cdnFallback.load(url, (from) => gltfLoader.loadAsync(from));
    stampGltfMaterialIndices(gltf.scene, gltf.parser.associations);
    return { scene: gltf.scene, animations: gltf.animations ?? [] };
  };
}
